/**
 * O editor de SQL das operações, com o CodeMirror 6 (ADR-0024).
 *
 * - O dialeto acompanha o tipo do banco da conexão; o autocompletar conhece as
 *   tabelas e colunas do mapa do banco lido por último.
 * - As cores vêm dos tokens (`var(--…)`), nos dois temas: nada da biblioteca
 *   escreve cor literal.
 * - A biblioteca fica só nesta página. O resto do produto nem a carrega.
 */

import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import { Compartment, EditorState } from '@codemirror/state';
import {
  EditorView,
  highlightActiveLine,
  highlightActiveLineGutter,
  keymap,
  lineNumbers,
  placeholder,
} from '@codemirror/view';
import { defaultKeymap, history, historyKeymap, indentWithTab } from '@codemirror/commands';
import { HighlightStyle, bracketMatching, syntaxHighlighting } from '@codemirror/language';
import { autocompletion, closeBrackets, completionKeymap } from '@codemirror/autocomplete';
import { MSSQL, MySQL, PostgreSQL, sql, type SQLNamespace } from '@codemirror/lang-sql';
import { tags } from '@lezer/highlight';
import type { Familia } from '../lib/operacoes';
import './editor-sql.css';

export interface TabelaDoMapa {
  readonly esquema: string;
  readonly nome: string;
  readonly colunas: readonly { readonly nome: string }[];
}

export interface EditorSqlRef {
  /** Põe o texto onde está o cursor, e volta o foco ao editor. */
  inserir: (texto: string) => void;
}

const DIALETO = { sqlserver: MSSQL, postgres: PostgreSQL, mysql: MySQL } as const;
const ESQUEMA_PADRAO: Partial<Record<Familia, string>> = { sqlserver: 'dbo', postgres: 'public' };

const realce = HighlightStyle.define([
  { tag: tags.keyword, color: 'var(--roxo)', fontWeight: '600' },
  { tag: [tags.string, tags.special(tags.string)], color: 'var(--ok)' },
  { tag: [tags.number, tags.bool, tags.null], color: 'var(--laranja-forte)' },
  { tag: [tags.lineComment, tags.blockComment], color: 'var(--texto-3)', fontStyle: 'italic' },
  { tag: [tags.special(tags.name), tags.variableName], color: 'var(--info)' },
  { tag: tags.operator, color: 'var(--texto-2)' },
]);

const tema = EditorView.theme({
  '&': { color: 'var(--texto)', backgroundColor: 'var(--superficie)' },
  '.cm-scroller': { minHeight: '220px', maxHeight: '60dvh' },
  '.cm-content': { caretColor: 'var(--roxo)', fontFamily: 'var(--fonte-mono)' },
  '.cm-cursor': { borderLeftColor: 'var(--roxo)' },
  '.cm-gutters': {
    backgroundColor: 'var(--superficie-2)',
    color: 'var(--texto-3)',
    borderRight: '1px solid var(--borda)',
  },
  '.cm-activeLine': { backgroundColor: 'var(--superficie-2)' },
  '.cm-activeLineGutter': { backgroundColor: 'var(--superficie-3)' },
  '&.cm-focused .cm-selectionBackground, .cm-selectionBackground, ::selection': {
    backgroundColor: 'var(--lavanda)',
  },
  '.cm-placeholder': { color: 'var(--texto-3)' },
  '.cm-tooltip': {
    border: '1px solid var(--borda)',
    backgroundColor: 'var(--superficie)',
    boxShadow: 'var(--sombra-2)',
  },
  '.cm-tooltip-autocomplete > ul > li[aria-selected]': {
    backgroundColor: 'var(--roxo-acao)',
    color: 'var(--sobre-roxo)',
  },
});

function esquemaDoMapa(tabelas: readonly TabelaDoMapa[]): SQLNamespace {
  const porEsquema: Record<string, Record<string, string[]>> = {};
  for (const t of tabelas) {
    (porEsquema[t.esquema] ??= {})[t.nome] = t.colunas.map((c) => c.nome);
  }
  return porEsquema;
}

function linguagem(familia: Familia, tabelas: readonly TabelaDoMapa[]) {
  const esquema = ESQUEMA_PADRAO[familia];
  return sql({
    dialect: DIALETO[familia],
    schema: esquemaDoMapa(tabelas),
    upperCaseKeywords: true,
    ...(esquema && tabelas.some((t) => t.esquema === esquema) ? { defaultSchema: esquema } : {}),
  });
}

export const EditorSql = forwardRef<
  EditorSqlRef,
  {
    valor: string;
    aoMudar: (valor: string) => void;
    familia: Familia;
    tabelas: readonly TabelaDoMapa[];
    rotulo: string;
    /** O id do texto de erro ou de ajuda, para o leitor de tela. */
    descritoPor?: string;
    invalido?: boolean;
    somenteLeitura?: boolean;
  }
>(function EditorSql(
  { valor, aoMudar, familia, tabelas, rotulo, descritoPor, invalido, somenteLeitura = false },
  ref,
) {
  const lugar = useRef<HTMLDivElement>(null);
  const vista = useRef<EditorView | null>(null);
  const aoMudarRef = useRef(aoMudar);
  aoMudarRef.current = aoMudar;
  const compartimentos = useRef({
    linguagem: new Compartment(),
    leitura: new Compartment(),
    atributos: new Compartment(),
  });

  const atributos = (): Record<string, string> => ({
    'aria-label': rotulo,
    'aria-multiline': 'true',
    ...(descritoPor ? { 'aria-describedby': descritoPor } : {}),
    ...(invalido ? { 'aria-invalid': 'true' } : {}),
  });

  // Nasce uma vez; as mudanças de fora entram pelos compartimentos abaixo.
  useEffect(() => {
    if (!lugar.current) return;
    const c = compartimentos.current;
    const v = new EditorView({
      parent: lugar.current,
      state: EditorState.create({
        doc: valor,
        extensions: [
          lineNumbers(),
          highlightActiveLineGutter(),
          highlightActiveLine(),
          history(),
          bracketMatching(),
          closeBrackets(),
          autocompletion(),
          syntaxHighlighting(realce),
          tema,
          placeholder('Escreva o SQL aqui'),
          keymap.of([...completionKeymap, ...defaultKeymap, ...historyKeymap, indentWithTab]),
          c.linguagem.of(linguagem(familia, tabelas)),
          c.leitura.of([
            EditorState.readOnly.of(somenteLeitura),
            EditorView.editable.of(!somenteLeitura),
          ]),
          c.atributos.of(EditorView.contentAttributes.of(atributos())),
          EditorView.updateListener.of((u) => {
            if (u.docChanged) aoMudarRef.current(u.state.doc.toString());
          }),
        ],
      }),
    });
    vista.current = v;
    return () => {
      v.destroy();
      vista.current = null;
    };
    // A vista nasce uma vez: o resto entra pelos compartimentos.
  }, []);

  useEffect(() => {
    vista.current?.dispatch({
      effects: compartimentos.current.linguagem.reconfigure(linguagem(familia, tabelas)),
    });
  }, [familia, tabelas]);

  useEffect(() => {
    vista.current?.dispatch({
      effects: compartimentos.current.leitura.reconfigure([
        EditorState.readOnly.of(somenteLeitura),
        EditorView.editable.of(!somenteLeitura),
      ]),
    });
  }, [somenteLeitura]);

  useEffect(() => {
    vista.current?.dispatch({
      effects: compartimentos.current.atributos.reconfigure(
        EditorView.contentAttributes.of(atributos()),
      ),
    });
  }, [rotulo, descritoPor, invalido]);

  // O valor que vem de fora (restaurar, trocar de operação) substitui o texto.
  useEffect(() => {
    const v = vista.current;
    if (!v || v.state.doc.toString() === valor) return;
    v.dispatch({ changes: { from: 0, to: v.state.doc.length, insert: valor } });
  }, [valor]);

  useImperativeHandle(ref, () => ({
    inserir: (texto) => {
      const v = vista.current;
      if (!v || somenteLeitura) return;
      const { from, to } = v.state.selection.main;
      v.dispatch({
        changes: { from, to, insert: texto },
        selection: { anchor: from + texto.length },
      });
      v.focus();
    },
  }));

  return (
    <div
      ref={lugar}
      className="editor-sql"
      data-invalido={invalido ? 'sim' : undefined}
      data-leitura={somenteLeitura ? 'sim' : undefined}
    />
  );
});
